import React from 'react';
import styled from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { CopyInput } from '~/components';
import { IconTransfers } from '~/elements/icons/transfers';
import { Flex, FormLabel, Inner, Text } from '~/styles';
import { formatDate, formatDateTime } from '~/util';

const Wrapper = styled.div`
  container-type: inline-size;
  container-name: interest-payout-summary;
`;

const PayoutSummary = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 12px;

  > * {
    min-width: 0;
  }

  @container interest-payout-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    grid-template-columns: auto min-content;
    gap: 16px;
    align-items: end;
  }

  @container interest-payout-summary (min-width: ${({ theme }) => theme.containerBreakpoints.desktop}) {
    grid-template-columns: minmax(0, 1fr) minmax(310px, min-content);
  }
`;

const PayoutAmount = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Amount = styled.h2`
  font-size: 24px;
  line-height: 24px;
  letter-spacing: -0.02em;
  font-weight: 600;
  display: flex;
  align-items: baseline;
  gap: 8px;
  color: ${({ theme }) => theme.foreground};
`;

const PayoutMeta = styled.div`
  position: relative;
  container-type: inline-size;
  padding: 0 24px;
  margin: 0 -24px;

  &:after {
    content: '';
    opacity: 0;
    transform: scaleX(0.75);
    transition:
      opacity 0.8s,
      transform 0.8s;
  }

  @container (max-width: 570px) {
    padding: 0 24px 0 12px;
    margin: 0 -24px 0 -12px;

    &:after {
      content: '';
      position: absolute;
      right: 24px;
      top: 0;
      bottom: 0;
      width: 1px;
      background: linear-gradient(
        to bottom,
        transparent 0%,
        ${({ theme }) => theme.secondary.blendToBackground(200)} 40%,
        ${({ theme }) => theme.secondary.blendToBackground(200)} 60%,
        transparent 100%
      );
      opacity: 1;
      transform: scaleX(1);
    }
  }

  ul {
    display: inline-flex;
    align-items: center;
    gap: 16px;
    list-style: none;
    line-height: 32px;
    box-sizing: border-box;
    margin: 0;
    flex-wrap: nowrap;
    white-space: nowrap;
    width: max-content;

    width: calc(100% + 24px);
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    position: relative;

    margin-left: -24px;
    padding-left: 24px;
    padding-right: 12px;

    mask: linear-gradient(to right, transparent 0%, black 24px, black calc(100% - 24px), transparent 100%);
    -webkit-mask: linear-gradient(to right, transparent 0%, black 24px, black calc(100% - 24px), transparent 100%);

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @container interest-payout-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    overflow-x: visible;
    overflow-y: visible;
    mask: none;
    -webkit-mask: none;
  }
`;

const DateTimeDisplay = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const DateTimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
  left: 0;
`;

const DateTimeUTC = styled(DateTimeLocal)`
  opacity: 0;
  position: relative;
`;

const DayLocal = styled.span`
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s ease-in-out;
`;

const DayUTC = styled(DayLocal)`
  opacity: 0;
`;

const DayDisplay = styled.div`
  position: relative;

  .icon-calendar-line {
    display: none;
  }
`;

const Date = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  &:hover {
    ${DateTimeLocal},
    ${DayLocal} {
      opacity: 0;
    }

    ${DateTimeUTC},
    ${DayUTC} {
      opacity: 1;
    }
  }
`;

const PayoutID = styled.div`
  width: 100%;
  min-width: 180px;
  box-sizing: border-box;
  height: fit-content;
  display: grid;
  grid-template-columns: min-content auto;
  align-items: center;
  align-self: flex-end;
  gap: 8px;

  border-radius: 8px;
  padding: 4px 4px 4px 12px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(200)};

  @container interest-payout-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    box-shadow: none;
    margin-bottom: 0;

    display: grid;
    grid-template-columns: auto;
    border: 0;
    gap: 8px;
    padding: 0;
  }
`;

const StyledCopyInput = styled(CopyInput)`
  --input-border-color: transparent;

  &:hover {
    --input-background: ${({ theme }) => theme.style.inputDefaultBackgroundColor};
    --input-border-color: ${({ theme }) => theme.style.inputDefaultHoveredBorderColor};
  }

  @container interest-payout-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    --input-border-color: inherit;
  }
`;

interface InterestPayoutPageHeaderProps {
  action?: React.ReactNode;
  amount: number | string;
  payoutDate?: string;
  currencyCode?: string;
  type?: 'credit' | 'debit';
  payoutId?: string;
  className?: string;
}

const getTypeChip = (type: 'credit' | 'debit' | undefined) => {
  switch (type) {
    case 'credit':
      return (
        <Chip icon={<Icon.CircleCheck />} size="large" variant="subtle" type="success">
          Credit
        </Chip>
      );
    case 'debit':
      return (
        <Chip icon={<IconTransfers.Debit />} size="large" variant="subtle">
          Debit
        </Chip>
      );
    default:
      return null;
  }
};

export const InterestPayoutPageHeader: React.FC<React.PropsWithChildren<InterestPayoutPageHeaderProps>> = ({
  action,
  amount,
  payoutDate,
  type,
  payoutId,
  currencyCode,
  className,
}) => {
  return (
    <Inner p={24} px="var(--page-margin)" pb={16} className={className}>
      <Wrapper>
        <PayoutSummary>
          <Flex flexDirection="column" gap={8}>
            <PayoutAmount>
              <Amount>
                {formatNumber(amount, currencyCode)}
                <Text size={14} color="lighter" weight="medium">
                  {currencyCode || 'USD'}
                </Text>
              </Amount>
            </PayoutAmount>

            <PayoutMeta>
              <ul>
                <li>
                  <Chip icon={<IconTransfers.InterestPayout />} size="large" variant="subtle">
                    Interest Payout
                  </Chip>
                </li>
                {type && <li>{getTypeChip(type)}</li>}
                {payoutDate && (
                  <li>
                    <Date>
                      <DayDisplay>
                        <DayLocal>
                          {formatDate(payoutDate, { dateFormat: 'short', timeZoneFormat: 'local' })
                            .split(' ')[1]
                            .replace(',', '')}
                        </DayLocal>
                        <DayUTC>
                          {formatDate(payoutDate, { dateFormat: 'short', timeZoneFormat: 'utc' })
                            .split(' ')[1]
                            .replace(',', '')}
                        </DayUTC>
                        <Icon.Calendar />
                      </DayDisplay>
                      <DateTimeDisplay>
                        <DateTimeLocal>
                          <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                            {formatDateTime(payoutDate, { timeZoneFormat: 'local' })}
                          </Text>
                        </DateTimeLocal>
                        <DateTimeUTC>
                          <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                            {formatDateTime(payoutDate, { timeZoneFormat: 'utc' })}
                          </Text>
                        </DateTimeUTC>
                      </DateTimeDisplay>
                    </Date>
                  </li>
                )}
              </ul>
            </PayoutMeta>
          </Flex>

          {payoutId && (
            <PayoutID>
              <FormLabel>Payout ID</FormLabel>
              <StyledCopyInput value={payoutId} size="small" isMonospaced />
            </PayoutID>
          )}

          {action && (
            <Flex alignItems="flex-end" flexDirection="row" mb={6}>
              {action}
            </Flex>
          )}
        </PayoutSummary>
      </Wrapper>
    </Inner>
  );
};
